import React from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ApiFetch } from '../App';
import { Survey } from './TableSurveysRow';

export interface AppUser {
  email: string;
  enabled: boolean;
  status: string;
  isAdmin: boolean;
  createdAt?: string;
}

type UsersDrawerProps = {
  open: boolean;
  onClose: () => void;
  apiFetch: ApiFetch;
  currentUserEmail: string;
  /** Surveys the admin can pick from to build the link inside the test email. */
  surveys: Survey[];
};

export default function UsersDrawer({
  open,
  onClose,
  apiFetch,
  currentUserEmail,
  surveys,
}: UsersDrawerProps) {
  const { t } = useTranslation();
  const [users, setUsers] = React.useState<AppUser[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);
  const [busyEmail, setBusyEmail] = React.useState<string>('');
  const [testEmail, setTestEmail] = React.useState('');
  const [testCompany, setTestCompany] = React.useState('');
  const [testSurvey, setTestSurvey] = React.useState<Survey | null>(null);
  const [sendingTest, setSendingTest] = React.useState(false);

  const loadUsers = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch({ operation: 'list_users' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error ?? 'error');
      setUsers(data.output as AppUser[]);
    } catch {
      setError(t('users.load_error'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch, t]);

  React.useEffect(() => {
    if (open) {
      setInfo(null);
      loadUsers();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const createUser = async () => {
    setCreating(true);
    setError(null);
    setInfo(null);
    try {
      const response = await apiFetch({ operation: 'create_user', targetEmail: newEmail.trim() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error ?? 'error');
      setInfo(t('users.created_info', { email: newEmail.trim() }));
      setNewEmail('');
      await loadUsers();
    } catch {
      setError(t('users.create_error'));
    } finally {
      setCreating(false);
    }
  };

  const runUserAction = async (email: string, body: Record<string, unknown>) => {
    setBusyEmail(email);
    setError(null);
    setInfo(null);
    try {
      const response = await apiFetch(body);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error ?? 'error');
      await loadUsers();
    } catch {
      setError(t('users.action_error'));
    } finally {
      setBusyEmail('');
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setError(null);
    setInfo(null);
    try {
      const response = await apiFetch({
        operation: 'send_test_email',
        targetEmail: testEmail.trim(),
        companyName: testCompany.trim(),
        surveyId: testSurvey!.i,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error ?? 'error');
      setInfo(t('users.test_email_sent', { email: testEmail.trim() }));
    } catch {
      setError(t('users.test_email_error'));
    } finally {
      setSendingTest(false);
    }
  };

  const testEmailValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim()) &&
    testCompany.trim() !== '' &&
    testSurvey !== null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2 }} role="presentation">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">{t('users.drawer_title')}</Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('users.drawer_intro')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label={t('users.new_email')}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            size="small"
            fullWidth
          />
          <Button
            variant="outlined"
            onClick={createUser}
            disabled={creating || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())}
          >
            {creating ? <CircularProgress size={20} /> : t('users.create_button')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {t('users.invite_hint')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {info}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }} p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <List dense>
            {users.map((user) => {
              const isSelf = user.email.toLowerCase() === currentUserEmail.toLowerCase();
              const busy = busyEmail === user.email;
              return (
                <ListItem
                  key={user.email}
                  divider
                  sx={{ px: 0, opacity: user.enabled ? 1 : 0.5 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={t('users.admin_toggle')}>
                        <span>
                          <Switch
                            size="small"
                            checked={user.isAdmin}
                            disabled={isSelf || busy}
                            onChange={(e) =>
                              runUserAction(user.email, {
                                operation: 'set_user_admin',
                                targetEmail: user.email,
                                makeAdmin: e.target.checked,
                              })
                            }
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title={user.enabled ? t('users.disable') : t('users.enable')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isSelf || busy}
                            onClick={() =>
                              runUserAction(user.email, {
                                operation: 'set_user_enabled',
                                targetEmail: user.email,
                                enabled: !user.enabled,
                              })
                            }
                          >
                            {user.enabled ? (
                              <BlockIcon fontSize="small" />
                            ) : (
                              <CheckCircleOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                      >
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {user.email}
                        </Typography>
                        {user.isAdmin && (
                          <Chip label={t('users.admin_chip')} size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      !user.enabled
                        ? t('users.status_disabled')
                        : user.status === 'FORCE_CHANGE_PASSWORD'
                          ? t('users.status_invited')
                          : undefined
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        <Typography variant="caption" color="text.secondary" display="block" mt={2}>
          {t('users.role_change_hint')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1">{t('users.test_email_title')}</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('users.test_email_intro')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label={t('users.test_email_recipient')}
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t('users.test_email_company')}
            value={testCompany}
            onChange={(e) => setTestCompany(e.target.value)}
            size="small"
            fullWidth
          />
          <Autocomplete
            options={surveys}
            value={testSurvey}
            onChange={(_, value) => setTestSurvey(value)}
            getOptionLabel={(survey) => `${survey.t} — ${survey.i}`}
            isOptionEqualToValue={(option, value) => option.i === value.i}
            renderInput={(params) => (
              <TextField {...params} label={t('users.test_email_survey')} size="small" />
            )}
          />
          <Button
            variant="outlined"
            onClick={sendTestEmail}
            disabled={sendingTest || !testEmailValid}
            sx={{ alignSelf: 'flex-start' }}
          >
            {sendingTest ? <CircularProgress size={20} /> : t('users.test_email_send')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
